import type { XtreamUserInfoResponse } from '../types';

/** Fetch never produced a `Response` (offline, CORS, reset, etc.). */
export const IPTV_PANEL_NETWORK_OR_UNREACHABLE =
  'Provider blocked browser access or the panel is unreachable.';

/** Panel or JSON indicates rejected credentials. */
export const IPTV_PANEL_INVALID_AUTH = 'Invalid username or password.';

/** Wrong host, path, port, or HTML instead of `player_api.php` JSON. */
export const IPTV_PANEL_INVALID_URL = 'Invalid IPTV server URL or panel endpoint.';

/** Response was read but did not match a clearer bucket. */
export const IPTV_PANEL_UNKNOWN = 'Unable to connect to IPTV panel.';

const isDev = (): boolean => process.env.NODE_ENV === 'development';

/** Max JSON we scan for auth keywords (login + typical error payloads stay tiny). */
const JSON_AUTH_SCAN_CHARS = 24_000;

/**
 * Dev-only: URL, status, body snippet, and how we classified the failure.
 * In production, callers should only surface the user-facing strings above.
 */
export function devPanelLoginLog(meta: {
  url: string;
  status?: number;
  snippet: string;
  classification: string;
  note?: string;
}): void {
  if (!isDev()) return;
  // eslint-disable-next-line no-console
  console.log('[VoraKit IPTV panel]', meta);
}

/** Strip UTF-8 BOM and trim so JSON.parse and substring checks behave. */
export function normalizePanelResponseText(bodyText: string): string {
  return String(bodyText).replace(/^\uFEFF/, '').trim();
}

/**
 * Phrases seen from Xtream-style panels, reverse proxies, and reseller UIs.
 * Applied to full body (text or JSON stringified) in lowercase.
 */
function textImpliesAuthFailure(lower: string): boolean {
  return (
    /\binvalid\s+username\b|\binvalid\s+password\b|invalid\s+username\s*[/\\]\s*password|invalid\s+credentials|wrong\s+credentials|bad\s+credentials|incorrect\s+username|incorrect\s+password|wrong\s+password|wrong\s+username|authentication\s+(error|failed|failure)|\bauth(?:entication)?\s+failed\b|login\s+failed|access\s+denied|not\s+authorized|\bunauthorized\b|\bcredential(s)?\b[^\n]{0,40}\b(invalid|wrong|incorrect)\b|\buser\b[^\n]{0,24}\b(invalid|disabled|banned|expired)\b|\baccount\b[^\n]{0,24}\b(expired|disabled|banned|inactive)\b|token\s+(expired|invalid)|session\s+(expired|invalid)|\b(check|verify)\b[^\n]{0,52}\b(username|password|credentials)\b/i.test(
      lower,
    ) || /\busername\s+or\s+password\b.*\b(wrong|invalid|incorrect)/i.test(lower)
  );
}

function getXtreamUserInfo(parsed: unknown): XtreamUserInfoResponse['user_info'] | undefined {
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return undefined;
  const ui = (parsed as XtreamUserInfoResponse).user_info;
  if (!ui || typeof ui !== 'object') return undefined;
  if (Object.keys(ui).length === 0) return undefined;
  return ui;
}

/** True when JSON carries numeric/string 401-style codes (common in reseller APIs). */
function looseObjectHttpAuthCodes(parsed: unknown): boolean {
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return false;
  const o = parsed as Record<string, unknown>;
  const codes = [o.error_code, o.errorCode, o.http_code, o.httpCode, o.statusCode, o.status];
  for (const c of codes) {
    if (c === 401 || c === 403 || c === '401' || c === '403') return true;
  }
  return false;
}

/**
 * For unknown JSON shapes: stringify a capped prefix and scan for auth phrases.
 * Used for login-sized bodies and small HTTP error bodies only — not huge category arrays.
 */
function looseJsonStringImpliesAuthFailure(parsed: unknown): boolean {
  if (parsed == null) return false;
  try {
    const s = JSON.stringify(parsed).slice(0, JSON_AUTH_SCAN_CHARS).toLowerCase();
    return textImpliesAuthFailure(s);
  } catch {
    return false;
  }
}

/**
 * Combined auth detection: raw body text, `user_info` (Xtream), HTTP codes in JSON, loose string scan.
 */
export function panelAuthFailureSignals(lowerBody: string, parsed: unknown | null): boolean {
  if (textImpliesAuthFailure(lowerBody)) return true;
  if (parsed != null) {
    if (looseObjectHttpAuthCodes(parsed)) return true;
    const ui = getXtreamUserInfo(parsed);
    if (ui) {
      const auth = ui.auth;
      if (auth === 0 || String(auth) === '0' || String(auth).toLowerCase() === 'false') {
        return true;
      }
      const st = ui.status;
      if (typeof st === 'string' && /banned|expired|disabled|invalid|inactive/i.test(st)) return true;
    }
    if (looseJsonStringImpliesAuthFailure(parsed)) return true;
  }
  return false;
}

function bodyLooksLikeHtml(text: string): boolean {
  const s = normalizePanelResponseText(text).toLowerCase();
  return s.startsWith('<!') || s.startsWith('<html') || s.includes('<!doctype html');
}

/** When `fetch` throws — there is no HTTP layer for us to inspect (typical CORS / offline). */
export function classifyPanelFetchThrow(e: unknown): string {
  const raw = e instanceof Error ? e.message : String(e);
  const m = raw.toLowerCase();

  if (/failed to fetch|networkerror|network request failed|load failed|econnrefused/i.test(m)) {
    return IPTV_PANEL_NETWORK_OR_UNREACHABLE;
  }
  if (/aborted|timeout|timed out|etimedout/i.test(m)) {
    return IPTV_PANEL_NETWORK_OR_UNREACHABLE;
  }
  if (/enotfound|getaddrinfo|name not resolved|dns|nxdomain/i.test(m)) {
    return IPTV_PANEL_INVALID_URL;
  }
  if (/ssl|certificate|cert_/i.test(m)) {
    return IPTV_PANEL_INVALID_URL;
  }
  return IPTV_PANEL_UNKNOWN;
}

/**
 * Map non-OK HTTP or unreadable success body to a user-facing line.
 * Used for `player_api.php` calls after login (lists, categories, etc.).
 */
export function classifyPanelHttpErrorResponse(status: number, bodyText: string): string {
  const text = normalizePanelResponseText(bodyText);
  const lower = text.toLowerCase();

  let parsed: unknown | null = null;
  if (text.startsWith('{') || text.startsWith('[')) {
    try {
      parsed = JSON.parse(text) as unknown;
    } catch {
      parsed = null;
    }
  }

  if (panelAuthFailureSignals(lower, parsed)) {
    return IPTV_PANEL_INVALID_AUTH;
  }
  if (status === 404 || status === 410 || status === 400) {
    return IPTV_PANEL_INVALID_URL;
  }
  if (bodyLooksLikeHtml(text)) {
    return IPTV_PANEL_INVALID_URL;
  }
  if (status === 401 || status === 403) {
    return IPTV_PANEL_INVALID_AUTH;
  }
  if (status >= 500 && status < 600) {
    return IPTV_PANEL_UNKNOWN;
  }
  return IPTV_PANEL_UNKNOWN;
}

function safeParseJsonValue(text: string): unknown | null {
  const t = normalizePanelResponseText(text);
  if (!t.startsWith('{') && !t.startsWith('[')) return null;
  try {
    return JSON.parse(t) as unknown;
  } catch {
    return null;
  }
}

export type XtreamLoginInterpret =
  | { ok: true; data: XtreamUserInfoResponse }
  | { ok: false; userMessage: string };

/**
 * Parse `player_api.php` login response (username+password, no `action`).
 * Only fetch-throw uses the CORS/unreachable copy — any HTTP status uses body classification.
 */
export function interpretXtreamLoginResponse(
  status: number,
  bodyText: string,
  requestUrl: string,
): XtreamLoginInterpret {
  const text = normalizePanelResponseText(bodyText);
  const lower = text.toLowerCase();
  const parsed = safeParseJsonValue(text);

  if (panelAuthFailureSignals(lower, parsed)) {
    devPanelLoginLog({
      url: requestUrl,
      status,
      snippet: text.slice(0, 500),
      classification: 'auth',
    });
    return { ok: false, userMessage: IPTV_PANEL_INVALID_AUTH };
  }

  if (status < 200 || status >= 300) {
    const msg = classifyPanelHttpErrorResponse(status, text);
    devPanelLoginLog({
      url: requestUrl,
      status,
      snippet: text.slice(0, 500),
      classification:
        msg === IPTV_PANEL_INVALID_AUTH ? 'auth_http' : msg === IPTV_PANEL_INVALID_URL ? 'bad_url_http' : 'http_other',
    });
    return { ok: false, userMessage: msg };
  }

  if (!parsed) {
    if (bodyLooksLikeHtml(text)) {
      devPanelLoginLog({
        url: requestUrl,
        status,
        snippet: text.slice(0, 500),
        classification: 'html_not_api',
      });
      return { ok: false, userMessage: IPTV_PANEL_INVALID_URL };
    }
    devPanelLoginLog({
      url: requestUrl,
      status,
      snippet: text.slice(0, 500),
      classification: 'not_json',
    });
    return { ok: false, userMessage: IPTV_PANEL_UNKNOWN };
  }

  const userInfo = getXtreamUserInfo(parsed);
  if (!userInfo) {
    devPanelLoginLog({
      url: requestUrl,
      status,
      snippet: text.slice(0, 500),
      classification: 'missing_user_info',
    });
    return { ok: false, userMessage: IPTV_PANEL_UNKNOWN };
  }

  devPanelLoginLog({
    url: requestUrl,
    status,
    snippet: text.slice(0, 500),
    classification: 'success',
  });
  return { ok: true, data: parsed as XtreamUserInfoResponse };
}
